import EditIcon from "@material-ui/icons/Edit";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Typography from "@material-ui/core/Typography";
import React, { FC, useState } from "react";

import BaseDataTable from "../../../../../components/modules/BaseDataTable";
import DomainList from "../DomainList";
import EditCustomDomainListModal from "../EditCustomDomainListModal";
import classes from "../../PublicationListSelectionSlide.module.scss";
import { BrandgenListDomain, BrandgenPublicationList } from "../../../../../types/campaignTypes";
import { truncateText } from "../../../../../helpers/truncateText";

const DASHBOARD_CONFIG = {
  public: {
    title: "BrandGen Publication Lists",
    description:
      "These are carefully curated lists of publications that we believe have a high likelihood of accepting ad traffic. If you do not wish to use any of these lists, you can create your own custom list instead.",
    nameMinWidth: 400,
    truncateLength: 100,
    containerClass: classes.domainListsDashboardContainer,
  },
  custom: {
    title: "Custom Publication Lists",
    emptyMessage: "These are no custom domain lists yet!",
    withListsMessage: "These are all of the lists that you've created previously",
    nameMinWidth: 350,
    truncateLength: 80,
    containerClass: classes.domainListsDashboardContainer,
  },
} as const;

export type DomainListsDashboardVariant = "public" | "custom";

type DomainListsDashboardProps = {
  variant: DomainListsDashboardVariant;
  domainLists: BrandgenPublicationList[];
  selectedDomainListsIds: string[];
  toggleSelectDomainLists: (listId: string) => void;
  updateDomainList?: (
    listId: string,
    listName: string,
    domainsToUpdate: BrandgenListDomain[],
    domainsToAdd: string[],
    saveUpdatedDomainListToStore: (updatedDomainLists: BrandgenPublicationList[]) => void,
  ) => void;
};

const DomainListsDashboard: FC<DomainListsDashboardProps> = ({
  variant,
  domainLists,
  selectedDomainListsIds,
  toggleSelectDomainLists,
  updateDomainList,
}) => {
  const [customDomainListToEdit, setCustomDomainListToEdit] = useState<BrandgenPublicationList | null>(null);
  const config = DASHBOARD_CONFIG[variant];
  const isCustom = variant === "custom";

  const handleOpenModal = (dataToEdit: BrandgenPublicationList) => () => setCustomDomainListToEdit(dataToEdit);
  const handleCloseModal = () => setCustomDomainListToEdit(null);

  const columns = [
    { key: "name" as const, label: "Name", minWidth: config.nameMinWidth },
    ...(isCustom ? [{ key: "actions" as const, label: "Actions", minWidth: 100 }] : []),
  ];

  const rows = domainLists
    .sort((a, b) => Date.parse(b.createdAt ?? "") - Date.parse(a.createdAt ?? ""))
    .map(({ id, name, listDomains }) => {
      const activeDomains = isCustom ? listDomains?.filter((d) => d.active === true) ?? [] : listDomains ?? [];

      return {
        id: id.toString(),
        name: (() => (
          <Tooltip title={name}>
            <Typography>{truncateText(name, config.truncateLength)}</Typography>
          </Tooltip>
        ))(),
        actions: (() => (
          <div className={classes.actions}>
            {isCustom && updateDomainList && (
              <IconButton
                className={classes.editButton}
                onClick={handleOpenModal(domainLists.find((l) => l.id === id) as BrandgenPublicationList)}
              >
                <EditIcon />
              </IconButton>
            )}
          </div>
        ))(),
        collapsableConfigs: {
          collapsable: true,
          collapsableChild: <DomainList domainList={activeDomains} />,
        },
      };
    });

  const description =
    variant === "public"
      ? DASHBOARD_CONFIG.public.description
      : domainLists.length === 0
      ? DASHBOARD_CONFIG.custom.emptyMessage
      : DASHBOARD_CONFIG.custom.withListsMessage;

  return (
    <div className={config.containerClass}>
      {isCustom && updateDomainList && (
        <EditCustomDomainListModal
          isOpen={Boolean(customDomainListToEdit)}
          handleClose={handleCloseModal}
          customDomainListToEdit={customDomainListToEdit}
          updateDomainList={updateDomainList}
        />
      )}

      <div className={classes.header}>
        <Typography variant="h2">{config.title}</Typography>
        <Typography>{description}</Typography>
      </div>

      <BaseDataTable columns={columns} rows={rows} />
    </div>
  );
};

export default DomainListsDashboard;
